import { hasPermission } from './navPermissions';
import { SETTINGS_NAV, PLATFORM_SETTINGS_NAV } from '../components/settings/SettingsSidebar';
import { canManagePlatformSettings, canManagePlatformRoles, isSuperAdmin as isCrmSuperAdmin } from './crmPermissions';

export const FULL_SETTINGS_NAV_ROLES = ['school_admin'];

/** Settings section id → permission keys required (any one grants access). */
export const SETTINGS_SECTION_PERMISSIONS = {
  'general.settings': ['manage_school'],
  'general.school': ['manage_school'],
  'general.idcard': ['manage_school'],
  'general.email': ['manage_school'],
  'general.whatsapp': ['manage_school'],
  'features.management': ['manage_school'],
  'slips.templates': ['manage_school'],
  'reports.certificates': ['manage_school'],
  'academic.year': ['manage_school'],
  'academic.attendance': ['manage_school'],
  'academic.biometric': ['manage_school', 'manage_biometric'],
  'roles.userRoles': ['manage_users'],
  'roles.permissions': ['manage_users'],
  'roles.staffAccess': ['manage_users'],
  'billing.subscription': ['manage_school'],
  'platform.branding': ['manage_platform_settings'],
  'platform.payment': ['manage_platform_settings'],
  'platform.sms': ['manage_platform_settings'],
  'platform.financialYear': ['manage_platform_settings'],
  'platform.security': ['manage_platform_settings'],
};

export function isSuperAdminSettingsUser(user) {
  return isCrmSuperAdmin(user) || canManagePlatformSettings(user) || canManagePlatformRoles(user);
}

export function hasFullSettingsNav(user) {
  return FULL_SETTINGS_NAV_ROLES.includes(user?.role_slug);
}

export function canAccessSettingsSection(user, sectionId) {
  if (!user || !sectionId) return false;
  if (isCrmSuperAdmin(user)) {
    return sectionId.startsWith('platform.');
  }
  if (sectionId.startsWith('platform.')) {
    const keys = SETTINGS_SECTION_PERMISSIONS[sectionId] || [];
    return keys.some((key) => hasPermission(user, key));
  }
  if (hasFullSettingsNav(user)) return true;
  const keys = SETTINGS_SECTION_PERMISSIONS[sectionId];
  if (!keys?.length) return false;
  return keys.some((key) => hasPermission(user, key));
}

/**
 * Settings menu groups: hide groups with zero accessible items; within visible
 * groups show only granted sections. School admin keeps full menu.
 */
export function resolveSettingsNav(user, nav = SETTINGS_NAV) {
  if (isCrmSuperAdmin(user) || canManagePlatformSettings(user) || canManagePlatformRoles(user)) {
    return PLATFORM_SETTINGS_NAV.map((group) => {
      const items = (group.items || []).filter((item) => canAccessSettingsSection(user, item.id));
      if (items.length === 0) return null;
      return { ...group, items };
    }).filter(Boolean);
  }
  if (hasFullSettingsNav(user)) return nav;

  return nav
    .map((group) => {
      const items = (group.items || []).filter((item) => canAccessSettingsSection(user, item.id));
      if (items.length === 0) return null;
      return { ...group, items };
    })
    .filter(Boolean);
}

export function firstAccessibleSettingsSection(user) {
  const nav = resolveSettingsNav(user);
  return nav[0]?.items[0]?.id ?? null;
}
